import { FunctionComponent, useContext } from "react";
import { Link, match } from "react-router-dom";
import styled, { Image, Button, Loader } from "grabcad-ui-elements";
import { IScreenProps } from "../../screens/Screen";
import { Breadcrumbs } from "../../view/Navigation/BreadCrumbs";
import { useUserGroupsQuery } from "../../graphql";
import { PATHS } from "../../Routes";
import { UserContext } from "../../components/User/UserProvider";
import PlaceholderSvg from "../../images/placeholder_user_groups.svg";
import {
    HighlightRow,
    InfoBox,
    StyledHeaderButton,
    StyledHeaderContainer,
} from "../../components/Styles";
import { ApplicationContext, type TranslationFunction } from "../../components/ApplicationProvider";
import { shortSoftwareVersion } from "../VersionManagement/utils/version_management_utils";

// TODO: Shared placeholder wrapper
const Placeholder = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
    img,
    p {
        margin: 0 auto;
    }
    img {
        height: 202px;
    }
    p {
        max-width: 300px;
        margin-bottom: 20px;
        font-size: 14px;
        line-height: 18px;
    }
    p,
    h2 {
        color: #666666;
    }
`;

const PlaceholderWrapper = styled.div`
    position: relative;
    // Todo: Refactor parent in Screen.tsx to avoid hard-coded pixel height
    height: calc(100vh - 100px);
`;

interface ICompanyUserGroupsProps extends IScreenProps {
    match: match<{ id?: string }>;
}

export const CompanyUserGroups: FunctionComponent<ICompanyUserGroupsProps> = (props) => {
    const { t } = useContext(ApplicationContext);
    const userContext = useContext(UserContext);
    const isCompanyAdmin = userContext.isCompanyAdmin;
    const { loadingUserGroups, userGroups } = useUserGroupsQuery();
    // TODO: Make single instance of <Breadcrumbs> in Shell? (See Shop's breadcrumbs)
    const breadcrumbs = <Breadcrumbs sections={[{ label: t("user_groups.breadcrumb") }]} />;

    const userGroupOptions = { number_of_groups: userGroups.length };
    const userGroupsTitle = (
        <h2 className="page-header">{t("user_groups.user_groups", userGroupOptions)}</h2>
    );

    if (loadingUserGroups) {
        return (
            <>
                {breadcrumbs}
                <Loader active />
            </>
        );
    }

    if (userGroups.length === 0) {
        return (
            <>
                {breadcrumbs}
                <PlaceholderWrapper>
                    {userGroupsTitle}
                    <Placeholder id="qa-companyUserGroups-placeholder">
                        <Image src={PlaceholderSvg} />
                        <h2>{t("user_groups.no_groups.title")}</h2>
                        <p>{t("user_groups.no_groups.content")}</p>
                        {isCompanyAdmin && (
                            <Button
                                id="qa-companyUserGroups-noGroups-createNewGroup"
                                className="ui button primary"
                                onClick={() => props.history.push(PATHS.newUserGroup)}
                            >
                                {t("user_groups.create_user_group")}
                            </Button>
                        )}
                    </Placeholder>
                </PlaceholderWrapper>
            </>
        );
    }

    return (
        <div style={{ paddingBottom: 25 }}>
            {breadcrumbs}
            <StyledHeaderContainer>
                {userGroupsTitle}
                {isCompanyAdmin && (
                    <StyledHeaderButton
                        id="qa-companyUserGroups-createNewGroup"
                        className="ui right button primary"
                        type="submit"
                        onClick={() => props.history.push(PATHS.newUserGroup)}
                    >
                        {t("user_groups.new_user_group")}
                    </StyledHeaderButton>
                )}
            </StyledHeaderContainer>
            <InfoBox>{t("user_groups.info")}</InfoBox>
            <table className="ui table qa-userGroups">
                <thead>
                    <tr>
                        <th>{t("user_groups.name")}</th>
                        <th>{t("user_groups.users")}</th>
                        <th>{t("user_groups.software_version")}</th>
                    </tr>
                </thead>
                <tbody>
                    {userGroups.map((userGroup) => {
                        const softwareVersion =
                            userGroup.softwareVersion?.selectedVersion?.productVersion;

                        return (
                            <HighlightRow
                                key={userGroup.id}
                                onClick={() =>
                                    props.history.push(`${PATHS.userGroup}/${userGroup.id}`)
                                }
                            >
                                <td data-label="User Group Name">{userGroup.name}</td>
                                <td data-label="Users">{userGroup.users.length}</td>
                                <td data-label="Software Version">
                                    {softwareVersion
                                        ? shortSoftwareVersion(softwareVersion)
                                        : t("version_management.groups.same_as_company_tag_name")}
                                </td>
                            </HighlightRow>
                        );
                    })}
                </tbody>
            </table>

            <VersionManagementInfoText>{renderVersionManagementInfo(t)}</VersionManagementInfoText>
        </div>
    );
};

const VersionManagementInfoText = styled.div`
    margin: -6px 0 0 12px;
    color: #6a6a6a;
`;

// Splits the translated string on the <link></link> placeholder tags so the
// "Version Management" text can be rendered as a real react-router Link.
// Newer versions of react-intl support rich text formatting, which would allow this to be done in a more elegant way, but we are using an older version that does not support that.
function renderVersionManagementInfo(t: TranslationFunction) {
    const text = t("user_groups.version_management_info");
    const [before, remainder] = text.split("<link>");
    const [linkText, after] = (remainder ?? "").split("</link>");

    return (
        <>
            {before}
            <Link to={PATHS.versionManagment}>{linkText}</Link>
            {after}
        </>
    );
}
